import React, { useEffect, useState } from 'react';
import { Volume2, Video, PhoneCall } from 'lucide-react';
import { useGroupCall } from '../../context/GroupCallContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useChat } from '../../context/ChatContext';
import type { GroupCallRoomDto } from '@sahaj/types';

export const ChannelCallBanner: React.FC = () => {
  const { currentWorkspace } = useWorkspace();
  const { activeChat } = useChat();
  const { activeGroupRoom, isJoined, joinGroupCall, fetchActiveChannelCall } = useGroupCall();
  const [activeChannelCall, setActiveChannelCall] = useState<GroupCallRoomDto | null>(null);

  const channelId = activeChat?.type === 'channel' ? activeChat.channel.id : null;

  useEffect(() => {
    if (!currentWorkspace || !channelId) {
      setActiveChannelCall(null);
      return;
    }

    if (isJoined && activeGroupRoom && activeGroupRoom.channelId === channelId) {
      setActiveChannelCall(activeGroupRoom);
      return;
    }

    let isMounted = true;
    fetchActiveChannelCall(currentWorkspace.id, channelId).then((room) => {
      if (isMounted) setActiveChannelCall(room);
    });

    const interval = setInterval(() => {
      if (!isJoined) {
        fetchActiveChannelCall(currentWorkspace.id, channelId).then((room) => {
          if (isMounted) setActiveChannelCall(room);
        });
      }
    }, 5000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [currentWorkspace, channelId, isJoined, activeGroupRoom, fetchActiveChannelCall]);

  if (!activeChannelCall || isJoined) return null;

  const count = activeChannelCall.participants?.length || 0;
  const isVideo = activeChannelCall.callType === 'VIDEO';

  const handleJoin = () => {
    if (!currentWorkspace || !channelId) return;
    joinGroupCall({
      workspaceId: currentWorkspace.id,
      channelId,
      callType: activeChannelCall.callType as 'AUDIO' | 'VIDEO',
    });
  };

  return (
    <div className="bg-primary/10 border-b border-primary/20 px-4 py-2 flex items-center justify-between gap-4 select-none">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/20 text-primary rounded-lg">
          {isVideo ? <Video className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-xs text-foreground">
              {isVideo ? 'Active Video Call' : 'Active Audio Huddle'}
            </span>
            <span className="text-[10px] bg-primary/20 text-primary px-1.5 py-0.5 rounded font-mono">
              {count}/4
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            {count === 1 ? '1 participant inside' : `${count} participants inside`}
          </p>
        </div>
      </div>

      <button
        onClick={handleJoin}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground font-medium text-xs rounded-lg hover:bg-primary/90 transition shadow-sm"
      >
        <PhoneCall className="h-3.5 w-3.5" />
        <span>Join Call</span>
      </button>
    </div>
  );
};
